<template>
  <div class="flex-1 overflow-auto p-4">
    <woot-button
      type="button"
      color-scheme="success"
      class-names="button--fixed-top"
      icon="add-circle"
      @click="openAddPopup"
    >
      {{ $t('LABEL_MGMT.HEADER_BTN_TXT') }}
    </woot-button>

    <!-- Toggle Tabs -->
    <div class="tabs-container">
      <button
        type="button"
        class="tab-button"
        :class="{ active: activeTab === 'all' }"
        @click="activeTab = 'all'"
      >
        All
      </button>
      <button
        type="button"
        class="tab-button"
        :class="{ active: activeTab === 'labels' }"
        @click="activeTab = 'labels'"
      >
        Manual
      </button>
      <button
        type="button"
        class="tab-button"
        :class="{ active: activeTab === 'automation' }"
        @click="activeTab = 'automation'"
      >
        Automation
      </button>
    </div>

    <div class="flex flex-row gap-4">
      <div class="w-[60%]">
        <woot-loading-state
          v-if="uiFlags.isFetching || !hasLoadedOnce"
        />
        <p
          v-else-if="!filteredRecords.length"
          class="flex h-full items-center flex-col justify-center"
        >
          {{ $t('LABEL_MGMT.LIST.404') }}
        </p>

        <table v-else class="woot-table">
          <thead>
            <th
              v-for="thHeader in $t('LABEL_MGMT.LIST.TABLE_HEADER')"
              :key="thHeader"
            >
              {{ thHeader }}
            </th>
          </thead>
          <tbody>
            <tr v-for="(label, index) in filteredRecords" :key="label.title">
              <td class="label-title">
                <span
                  v-tooltip="label.title.length > 25 ? label.title : null"
                  class="overflow-hidden whitespace-nowrap text-ellipsis"
                >
                  {{ label.title }}
                </span>
              </td>
              <td class="label-description">
                <span
                  v-tooltip.right="label.description?.length > 40 ? label.description : null"
                  class="overflow-hidden whitespace-nowrap text-ellipsis"
                >
                  {{ label.description }}
                </span>
              </td>
              <td>
                <div class="label-color--container">
                  <!-- Automation robot icon for automation labels -->
                  <div v-if="label.automation || label.contact_automation" class="automation-icon-wrap">
                    <svg
                      class="automation-robot-icon"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                      :style="{ color: label.color }"
                    >
                      <path d="M13 2v2h-2V2h2zm-1 4a6 6 0 0 0-6 6v6a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-6a6 6 0 0 0-6-6zM8 12h2v2H8v-2zm6 0h2v2h-2v-2zm-5 5h6v2H9v-2zM2 10h2v6H2v-6zm18 0h2v6h-2v-6z"/>
                    </svg>
                  </div>
                  <!-- Color square for normal labels -->
                  <span
                    v-else
                    class="label-color--display"
                    :style="{ backgroundColor: label.color }"
                  />
                  {{ label.color }}
                </div>
              </td>
              <td class="button-wrapper">
                <woot-button
                  type="button"
                  v-tooltip.top="$t('LABEL_MGMT.FORM.EDIT')"
                  variant="smooth"
                  size="tiny"
                  color-scheme="secondary"
                  class-names="grey-btn"
                  :is-loading="editLoading[label.id]"
                  icon="edit"
                  @click="openEditPopup(label)"
                />
                <woot-button
                  type="button"
                  v-tooltip.top="$t('LABEL_MGMT.FORM.DELETE')"
                  variant="smooth"
                  color-scheme="alert"
                  size="tiny"
                  icon="dismiss-circle"
                  class-names="grey-btn"
                  :is-loading="deleteLoading[label.id]"
                  @click="openDeletePopup(label, index)"
                />
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="w-[34%]">
        <span v-dompurify-html="$t('LABEL_MGMT.SIDEBAR_TXT')" />
      </div>
    </div>
    <woot-modal :show.sync="showAddPopup" :on-close="hideAddPopup" :show-close-button="false" size="modal-medium">
      <add-label @close="hideAddPopup" />
    </woot-modal>

    <woot-modal :show.sync="showEditPopup" :on-close="hideEditPopup" :show-close-button="false" size="modal-medium">
      <edit-label
        :selected-response="selectedResponse"
        @close="hideEditPopup"
      />
    </woot-modal>

    <woot-delete-modal
      :show.sync="showDeleteConfirmationPopup"
      :on-close="closeDeletePopup"
      :on-confirm="confirmDeletion"
      :title="$t('LABEL_MGMT.DELETE.CONFIRM.TITLE')"
      :message="$t('LABEL_MGMT.DELETE.CONFIRM.MESSAGE')"
      :message-value="deleteMessage"
      :confirm-text="deleteConfirmText"
      :reject-text="deleteRejectText"
    />
  </div>
</template>
<script>
import { mapGetters } from 'vuex';

import AddLabel from './AddLabel';
import EditLabel from './EditLabel';
import alertMixin from 'shared/mixins/alertMixin';

export default {
  components: {
    AddLabel,
    EditLabel,
  },
  mixins: [alertMixin],
  data() {
    return {
      editLoading: {},
      deleteLoading: {},
      showAddPopup: false,
      showEditPopup: false,
      showDeleteConfirmationPopup: false,
      selectedResponse: {},
      activeTab: 'all', // default
      hasLoadedOnce: false,
    };
  },
  computed: {
    ...mapGetters({
      records: 'labels/getLabels',
      uiFlags: 'labels/getUIFlags',
    }),
    filteredRecords() {
        // ALL tab → return everything
      if (this.activeTab === 'all') {
       return this.records;
       }
      if (this.activeTab === 'automation') {
        // Show only labels with automation or contact_automation enabled
        return this.records.filter(label => label.automation || label.contact_automation);
      }
      // Show only normal labels (no automation or contact_automation)
      return this.records.filter(label => !label.automation && !label.contact_automation);
    },
    // Delete Modal
    deleteConfirmText() {
      return this.$t('LABEL_MGMT.DELETE.CONFIRM.YES');
    },
    deleteRejectText() {
      return this.$t('LABEL_MGMT.DELETE.CONFIRM.NO');
    },
    deleteMessage() {
      return ` ${this.selectedResponse.title}?`;
    },
  },
  created() {
    Promise.all([
      this.$store.dispatch('labels/get'),
      this.$store.dispatch('labels/getlabelsindex'),
    ]).finally(() => {
      this.hasLoadedOnce = true;
    });
  },
  methods: {
    openAddPopup() {
      this.showAddPopup = true;
    },
    hideAddPopup() {
      this.showAddPopup = false;
    },

    async openEditPopup(response) {
      this.$set(this.editLoading, response.id, true);
      try {
        const responsedata = await this.$store.dispatch(
          'labels/getLabelsdata',
          response.id
        );
        this.selectedResponse = responsedata || response;
        this.showEditPopup = true;
      } finally {
        this.$set(this.editLoading, response.id, false);
      }
    },
    hideEditPopup() {
      this.showEditPopup = false;
    },

    openDeletePopup(response) {
      this.showDeleteConfirmationPopup = true;
      this.selectedResponse = response;
    },
    closeDeletePopup() {
      this.showDeleteConfirmationPopup = false;
    },

    confirmDeletion() {
      this.$set(this.deleteLoading, this.selectedResponse.id, true);
      this.closeDeletePopup();
      this.deleteLabel(this.selectedResponse.id);
    },
    // deleteLabel(id) {
    //   this.$store
    //     .dispatch('labels/delete', id)
    //     .then(() => {
    //       this.showAlert(this.$t('LABEL_MGMT.DELETE.API.SUCCESS_MESSAGE'));
    //     })
    //     .catch(() => {
    //       this.showAlert(this.$t('LABEL_MGMT.DELETE.API.ERROR_MESSAGE'));
    //     })
    //     .finally(() => {
    //       this.loading[this.selectedResponse.id] = false;
    //     });
    // },

    deleteLabel(id) {
      this.$store
        .dispatch('labels/delete', id)
        .then(() => {
          this.showAlert(this.$t('LABEL_MGMT.DELETE.API.SUCCESS_MESSAGE'));
        })
        .catch(error => {
          const apiMessage = error?.response?.data?.message;

          if (
            error?.response?.status === 422 &&
            apiMessage === 'Can not delete the priority label'
          ) {
            this.showAlert(apiMessage);
          } else {
            this.showAlert(this.$t('LABEL_MGMT.DELETE.API.ERROR_MESSAGE'));
          }
        })
        .finally(() => {
          this.$set(this.deleteLoading, id, false);
        });
    },

  },
};
</script>

<style scoped lang="scss">
@import 'dashboard/assets/scss/variables';

.tabs-container {
  display: flex;
  gap: 12px;
}

.tab-button {
  min-width: auto;
  text-align: center;
  padding: 12px;
  border: none;
  cursor: pointer;
  background-color: transparent;
  color: #151718; 
  transition: background-color 0.2s ease, color 0.2s ease;
  //box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  line-height:1.2;
  margin-bottom:8px;
  margin-top:8px;
  font-weight:600;
}

.tab-button:hover {
  background-color: #F1F3F5;
}

.tab-button.active {
  background-color: transparent;
  color: #40518A;
  border-bottom: 3px solid #40518A;
}

.label-color--container {
  @apply flex items-center;
}

.label-color--display {
  @apply rounded h-4 w-4 mr-1 rtl:mr-0 rtl:ml-1 border border-solid border-slate-50 dark:border-slate-700;
}

.automation-icon-wrap {
  @apply inline-flex items-center justify-center mr-1 rtl:mr-0 rtl:ml-1;
}

.automation-robot-icon {
  @apply w-4 h-4;
}
.label-title {
  span {
    @apply w-60 inline-block;
  }
}
.label-description{
  span {
    @apply w-60 inline-block;
  }
}
.woot-table td {
  vertical-align: top;
}
</style>
